'use client'

import { useId } from 'react'

interface LogoProps {
  width?: number
  height?: number
  className?: string
}

export function Logo({ width = 220, height = 52, className = '' }: LogoProps) {
  const id = useId()

  // Icon is 48×48 (scaled from 128×128 mark), centered in 60px tall viewBox.
  // The mark acts as the letter "O": [mark]rbit + flow
  return (
    <div className={className} style={{ width, height }}>
      <svg
        viewBox="0 0 278 60"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        fill="none"
        aria-label="OrbitFlow"
        overflow="visible"
      >
        <defs>
          {/* Gradient driven by the active theme's --primary variable so the
              logo always matches the user's selected color */}
          <linearGradient
            id={`${id}-g`}
            x1="19" y1="13" x2="109" y2="115"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%"   style={{ stopColor: 'hsl(var(--primary))', stopOpacity: 0.7 }} />
            <stop offset="55%"  style={{ stopColor: 'hsl(var(--primary))', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: 'hsl(var(--primary))', stopOpacity: 1 }} />
          </linearGradient>

          {/* Mask that cuts the top-left of the large orbit ring,
              leaving a gap where the satellite circle sits */}
          <mask id={`${id}-cut`}>
            <rect width="128" height="128" fill="white" />
            <circle cx="29" cy="34" r="24" fill="black" />
          </mask>
        </defs>

        {/* Orbit mark — 128×128 source scaled to 48×48, vertically centred */}
        <g transform="translate(0, 6) scale(0.375)">
          {/* Large orbit ring, masked to create the open arc */}
          <circle
            cx="64" cy="64" r="44"
            stroke={`url(#${id}-g)`}
            strokeWidth="16"
            mask={`url(#${id}-cut)`}
          />
          {/* Satellite circle at the gap */}
          <circle
            cx="29" cy="34" r="14"
            stroke={`url(#${id}-g)`}
            strokeWidth="16"
          />
        </g>

        {/* Wordmark: mark acts as "O", then "rbit" bold + "flow" dimmed */}
        <text
          y="46"
          style={{
            fontFamily: '"Inter", system-ui, sans-serif',
            fontSize: 42,
            letterSpacing: '-0.5',
          }}
        >
          <tspan x="52" fill="currentColor" fontWeight={700}>rbit</tspan>
          <tspan fill="currentColor" fontWeight={400} opacity={0.45}>flow</tspan>
        </text>
      </svg>
    </div>
  )
}
